const GQL_URL = process.env.GQL_URL || 'http://localhost:3002/api/v1/graphql'

// Uploaded images live on the API origin (e.g. https://api.example.com/uploads/images/<uuid>.png)
export const API_ORIGIN = new URL(GQL_URL).origin

export type ImageDimensionError = 'ratio' | 'tooSmall' | 'tooLarge'

export class ImageUploadError extends Error {
  constructor(
    public readonly code: 'type' | 'size' | 'pixels' | 'forbidden' | 'server' | ImageDimensionError,
  ) {
    super(code)
    this.name = 'ImageUploadError'
  }
}

export interface UploadLimits {
  maxBytes: number
  // Most pixels the API will decode; a small file can still be over it
  maxPixels?: number
  acceptedTypes: string[]
}

let limitsRequest: Promise<UploadLimits> | null = null

/**
 * The size limit and accepted types come from the API (GET /api/v1/uploads/images/limits),
 * so changing them only touches the API and nginx. The answer is cached for the page's life;
 * a failed request is retried on the next call.
 */
export function fetchUploadLimits(): Promise<UploadLimits> {
  if (!limitsRequest) {
    // Starting inside then() turns a synchronous fetch failure into a rejection too
    limitsRequest = Promise.resolve()
      .then(() => fetch(`${API_ORIGIN}/api/v1/uploads/images/limits`, { credentials: 'include' }))
      .then(async res => {
        if (!res.ok) {
          throw new Error(`limits request failed: ${res.status}`)
        }
        return (await res.json()) as UploadLimits
      })
      .catch(err => {
        limitsRequest = null
        throw err
      })
  }
  return limitsRequest
}

// Test hook: forget the cached limits
export function resetUploadLimitsCache() {
  limitsRequest = null
}

// "image/jpeg" -> "JPG", "image/webp" -> "WEBP"
export function imageTypeLabels(types: string[]): string {
  return types
    .map(type => {
      const subtype = type.split('/')[1] ?? type
      return subtype === 'jpeg' ? 'JPG' : subtype.toUpperCase()
    })
    .join(', ')
}

export function bytesToMegabytes(bytes: number): number {
  return Math.round((bytes / (1024 * 1024)) * 10) / 10
}

/** Pixel rules a form can put on its image. All are optional and combine. */
export interface ImageDimensionRules {
  // Proportions, e.g. { width: 800, height: 301 } for banners or { width: 1, height: 1 } for a QR code
  aspectRatio?: { width: number; height: number }
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
}

// Allows 1% off so a resized or cropped image that rounds a pixel still passes
const ASPECT_RATIO_TOLERANCE = 0.01

export function checkImageDimensions(
  size: { width: number; height: number },
  rules: ImageDimensionRules,
): ImageDimensionError | null {
  const { aspectRatio, minWidth = 0, minHeight = 0, maxWidth, maxHeight } = rules
  if (aspectRatio) {
    const expected = aspectRatio.width / aspectRatio.height
    const actual = size.width / size.height
    if (Math.abs(actual - expected) / expected > ASPECT_RATIO_TOLERANCE) {
      return 'ratio'
    }
  }
  if (size.width < minWidth || size.height < minHeight) {
    return 'tooSmall'
  }
  if ((maxWidth && size.width > maxWidth) || (maxHeight && size.height > maxHeight)) {
    return 'tooLarge'
  }
  return null
}

// Uses an <img> rather than createImageBitmap so it works in older Safari too
export function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new ImageUploadError('type'))
    }
    img.src = url
  })
}

// The form an image is for; the API only accepts the upload if the user may create or edit it
// in the given application
export type ImageUploadTarget =
  | 'collaborators'
  | 'departments'
  | 'locations'
  | 'banners'
  | 'events'
  | 'offerings'
  | 'feed'

/**
 * Sends the file as a raw body to POST /api/v1/uploads/images and returns the stored path
 * (e.g. /uploads/images/<uuid>.png), which is what the GraphQL image fields accept.
 */
export async function uploadImage(
  file: File,
  target: ImageUploadTarget,
  applicationId: string,
): Promise<string> {
  // Fail fast when the limits are known; the API enforces them either way
  const limits = await fetchUploadLimits().catch(() => null)
  if (limits && !limits.acceptedTypes.includes(file.type)) {
    throw new ImageUploadError('type')
  }
  if (limits && file.size > limits.maxBytes) {
    throw new ImageUploadError('size')
  }
  const query = new URLSearchParams({ for: target, application: applicationId })
  const res = await fetch(`${API_ORIGIN}/api/v1/uploads/images?${query}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': file.type, 'X-Csrf-Protection': '1' },
    body: file,
  })
  if (!res.ok) {
    if (res.status === 413 && (await tooManyPixels(res))) {
      throw new ImageUploadError('pixels')
    }
    const code =
      res.status === 413
        ? 'size'
        : res.status === 415
          ? 'type'
          : res.status === 401 || res.status === 403
            ? 'forbidden'
            : 'server'
    throw new ImageUploadError(code)
  }
  const data = (await res.json()) as { path?: string }
  if (!data.path) {
    throw new ImageUploadError('server')
  }
  return data.path
}

// Same naming rule as the API's lowResPathFor: /uploads/images/<uuid>.png → /uploads/images/<uuid>.low.webp
const STORED_IMAGE_PATH = /^(\/uploads\/images\/[0-9a-f-]{36})\.(jpg|png|gif|webp)$/

/**
 * The low-res copy the API keeps for a stored image, or null for no image. Lets an optimistic
 * response carry the copy of a just-uploaded image before the server answers.
 */
export function lowResPathFor(imagePath: string | null | undefined): string | null {
  const match = imagePath ? STORED_IMAGE_PATH.exec(imagePath) : null
  return match ? `${match[1]}.low.webp` : null
}

// The API answers 413 both for too many bytes (body parser) and too many pixels (decoder)
async function tooManyPixels(res: Response) {
  const body = (await res.json().catch(() => null)) as { errors?: { code?: string }[] } | null
  return body?.errors?.[0]?.code === 'TOO_MANY_PIXELS'
}

// Stored values are API-relative paths; prefix the API origin for <img src>
export function imageUrl(path: string | null | undefined): string {
  if (!path) {
    return ''
  }
  return path.startsWith('/') ? `${API_ORIGIN}${path}` : path
}
